import { Box, Typography, Chip, Divider, Grid } from '@mui/material'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import RecursiveProperty from '../../../../components/utilities/RenderJSON'
import AmlddSearchTerms from '../KybToPdf/checks/AmlddSearchTerms'

const AmlddCheckDetail = ({ checkType, checkDetails, theme }) => {
  const { t } = useTranslation(['common'])
  const [expandedRecords, setExpandedRecords] = useState({})

  const handleToggleRecord = (index) => {
    setExpandedRecords((prev) => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  if (!checkDetails) {
    return (
      <Box className="recursive-property" sx={{ backgroundColor: theme === 'light' ? '#FCFFFD' : '#020D06', p: 2 }}>
        <Typography>{t('common:noDataAvailable')}</Typography>
      </Box>
    )
  }

  const { quantidade = 0, busca = [], cadastros = [] } = checkDetails

  // Si no hay registros encontrados
  if (quantidade === 0 || cadastros.length === 0) {
    return (
      <Box className="recursive-property" sx={{ backgroundColor: theme === 'light' ? '#FCFFFD' : '#020D06', p: 2 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          <strong>{t('common:amldd.totalRecords')}:</strong> {quantidade}
        </Typography>
        <Typography color="success.main" fontWeight="bold">
          {t('common:amldd.noRecordsFound')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ backgroundColor: theme === 'light' ? '#FCFFFD' : '#020D06', p: 2 }}>
      {/* Summary */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, fontSize: '1.25rem' }}>
          {t('common:amldd.summary')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>{t('common:amldd.totalRecords')}:</strong>{' '}
              <Chip
                label={quantidade > 0 ? quantidade : t('common:amldd.noRecordsFound')}
                color={quantidade > 0 ? 'error' : 'success'}
                size="small"
                sx={{ ml: 1 }}
              />
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Search Terms */}
      {busca && busca.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('common:amldd.searchTerms')}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <AmlddSearchTerms busca={busca} showTitle={false} />
          </Box>
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {/* Records Found */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t('common:amldd.recordsFound')} ({cadastros.length})
      </Typography>

      {cadastros.map((record, index) => (
        <Box
          key={record.id || index}
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: theme === 'light' ? '#f5f5f5' : '#0d2314',
            borderRadius: 1
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: expandedRecords[index] ? 2 : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                {record.nome || `${t('common:amldd.record')} ${index + 1}`}
              </Typography>
              <Box
                component="span"
                onClick={() => handleToggleRecord(index)}
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  color: 'primary.main',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {t('common:seeMore')}
                <KeyboardArrowRightIcon sx={{ fontSize: '1rem' }} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {checkType === 'AMLDD_ESG' && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {record.qtdAbonadoras !== undefined && (
                    <Chip
                      label={`${t('common:amldd.positive')}: ${record.qtdAbonadoras}`}
                      color="success"
                      size="small"
                      sx={{ '& .MuiChip-label': { color: 'white' } }}
                    />
                  )}
                  {record.qtdDesabonadoras !== undefined && (
                    <Chip
                      label={`${t('common:amldd.negative')}: ${record.qtdDesabonadoras}`}
                      color="error"
                      size="small"
                    />
                  )}
                </Box>
              )}
              {checkType === 'AMLDD_PEP' && record.classificacao && (
                <Chip
                  label={record.classificacao === 'T' ? t('common:amldd.holder') : t('common:amldd.related')}
                  color={record.classificacao === 'T' ? 'error' : 'warning'}
                  size="small"
                />
              )}
              {checkType === 'AMLDD_LRI' && record.origem && (
                <Chip
                  label={record.origem}
                  color="error"
                  size="small"
                />
              )}
            </Box>
          </Box>

          {expandedRecords[index] && (
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme === 'light' ? '#e0e0e0' : '#1a2e1f'}` }}>
              {renderRecordDetails(record, checkType, t, theme)}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  )
}

// Configuration for MTLSN variants
const MTLSN_FIELDS_CONFIG = {
  'AMLDD_MTLSN_SIMPLE': {
    fields: ['nome', 'crimes', 'citado_midia', 'data'],
    showNews: false
  },
  'AMLDD_MTLSN': {
    fields: ['nome', 'crimes', 'citado_midia', 'data', 'documentos', 'pseudonimos', 'envolvimentos', 'listas', 'pep', 'score'],
    showNews: false
  },
  'AMLDD_MTLSN_WITH_NEWS': {
    fields: ['nome', 'crimes', 'citado_midia', 'data', 'documentos', 'pseudonimos', 'envolvimentos', 'listas', 'pep', 'score', 'noticias'],
    showNews: true
  }
}

const renderMtlsnFields = (record, config, t) => {
  return (
    <Box>
      <Grid container spacing={2}>
        {/* Nome - Always shown in accordion summary, can be hidden here */}

        {/* Score */}
        {config.fields.includes('score') && record.score !== undefined && (
          <Grid item xs={12} md={6}>
            <Typography><strong>{t('common:amldd.score')}:</strong> {record.score}</Typography>
          </Grid>
        )}

        {/* Citado na Mídia */}
        {config.fields.includes('citado_midia') && record.citado_midia !== undefined && (
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>{t('common:amldd.mentionedInMedia')}:</strong>{' '}
              <Chip
                label={record.citado_midia ? t('common:yes') : t('common:no')}
                color={record.citado_midia ? 'error' : 'default'}
                size="small"
              />
            </Typography>
          </Grid>
        )}

        {/* PEP */}
        {config.fields.includes('pep') && record.pep !== undefined && (
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>{t('common:amldd.isPep')}:</strong>{' '}
              <Chip
                label={record.pep ? t('common:yes') : t('common:no')}
                color={record.pep ? 'warning' : 'default'}
                size="small"
              />
            </Typography>
          </Grid>
        )}

        {/* Documentos */}
        {config.fields.includes('documentos') && record.documentos && (
          <Grid item xs={12}>
            <Typography><strong>{t('common:amldd.documents')}:</strong> {record.documentos}</Typography>
          </Grid>
        )}

        {/* Pseudônimos */}
        {config.fields.includes('pseudonimos') && record.pseudonimos && (
          <Grid item xs={12}>
            <Typography><strong>{t('common:amldd.aliases')}:</strong> {record.pseudonimos}</Typography>
          </Grid>
        )}

        {/* Envolvimentos */}
        {config.fields.includes('envolvimentos') && record.envolvimentos && (
          <Grid item xs={12}>
            <Typography><strong>{t('common:amldd.involvements')}:</strong> {record.envolvimentos}</Typography>
          </Grid>
        )}

        {/* Crimes */}
        {config.fields.includes('crimes') && record.crimes && (
          <Grid item xs={12}>
            <Typography color="error">
              <strong>{t('common:amldd.crimes')}:</strong>
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', ml: 2 }}>
              {record.crimes.split('|').join('\n')}
            </Typography>
          </Grid>
        )}

        {/* Listas */}
        {config.fields.includes('listas') && record.listas && (
          <Grid item xs={12}>
            <Typography><strong>{t('common:amldd.lists')}:</strong> {record.listas}</Typography>
          </Grid>
        )}

        {/* Notícias (only for NEWS variant) */}
        {config.showNews && record.noticias && Array.isArray(record.noticias) && (
          <Grid item xs={12}>
            <Typography color="primary" sx={{ mb: 1 }}>
              <strong>{t('common:amldd.news')}:</strong>
            </Typography>
            <Box sx={{ ml: 2 }}>
              {record.noticias.map((noticia, idx) => (
                <Box key={idx} sx={{ mb: 2, p: 2, backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                  {noticia.titulo && (
                    <Typography variant="subtitle2" fontWeight="bold">{noticia.titulo}</Typography>
                  )}
                  {noticia.conteudo && (
                    <Typography variant="body2" sx={{ mt: 1 }}>{noticia.conteudo}</Typography>
                  )}
                  {noticia.fonte && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {t('common:amldd.source')}: {noticia.fonte}
                    </Typography>
                  )}
                  {noticia.data && (
                    <Typography variant="caption" color="text.secondary">
                      {t('common:amldd.date')}: {new Date(noticia.data).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Grid>
        )}

        {/* Data */}
        {config.fields.includes('data') && record.data && (
          <Grid item xs={12}>
            <Typography variant="caption">
              <strong>{t('common:amldd.date')}:</strong> {new Date(record.data).toLocaleString()}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}

const renderRecordDetails = (record, checkType, t, theme) => {
  // AMLDD_ESG specific fields
  if (checkType === 'AMLDD_ESG') {
    return (
      <Box>
        <Grid container spacing={2}>
          {record.documentos && (
            <Grid item xs={12} md={6}>
              <Typography><strong>{t('common:amldd.documents')}:</strong> {record.documentos}</Typography>
            </Grid>
          )}
          {record.perfil && (
            <Grid item xs={12} md={6}>
              <Typography><strong>{t('common:amldd.profile')}:</strong> {record.perfil}</Typography>
            </Grid>
          )}
          {record.enquadramentos && (
            <Grid item xs={12}>
              <Typography><strong>{t('common:amldd.classifications')}:</strong> {record.enquadramentos}</Typography>
            </Grid>
          )}
          {record.pseudonimos && (
            <Grid item xs={12}>
              <Typography><strong>{t('common:amldd.aliases')}:</strong> {record.pseudonimos}</Typography>
            </Grid>
          )}
          {record.titulosDesabonadores && (
            <Grid item xs={12}>
              <Typography color="error"><strong>{t('common:amldd.negativeTitles')}:</strong></Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', ml: 2 }}>
                {record.titulosDesabonadores.split('|').join('\n')}
              </Typography>
            </Grid>
          )}
          {record.titulosAbonadores && (
            <Grid item xs={12}>
              <Typography color="success.main" ><strong>{t('common:amldd.positiveTitles')}:</strong></Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', ml: 2 }}>
                {record.titulosAbonadores.split('|').join('\n')}
              </Typography>
            </Grid>
          )}
          {record.ultimaAtualizacao && (
            <Grid item xs={12}>
              <Typography variant="caption">
                <strong>{t('common:amldd.lastUpdate')}:</strong> {new Date(record.ultimaAtualizacao).toLocaleString()}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    )
  }

  // AMLDD_LRI specific fields
  if (checkType === 'AMLDD_LRI') {
    return (
      <Box>
        <Grid container spacing={2}>
          {record.origem && (
            <Grid item xs={12} md={6}>
              <Typography><strong>{t('common:amldd.source')}:</strong> {record.origem}</Typography>
            </Grid>
          )}
          {record.score !== undefined && (
            <Grid item xs={12} md={6}>
              <Typography><strong>{t('common:amldd.score')}:</strong> {record.score}</Typography>
            </Grid>
          )}
          {record.documentos && (
            <Grid item xs={12}>
              <Typography><strong>{t('common:amldd.documents')}:</strong> {record.documentos}</Typography>
            </Grid>
          )}
          {record.pseudonimos && (
            <Grid item xs={12}>
              <Typography><strong>{t('common:amldd.aliases')}:</strong> {record.pseudonimos}</Typography>
            </Grid>
          )}
          {record.datasNascimento && (
            <Grid item xs={12} md={6}>
              <Typography><strong>{t('common:amldd.birthDate')}:</strong> {new Date(record.datasNascimento).toLocaleDateString()}</Typography>
            </Grid>
          )}
          {record.locaisNascimento && (
            <Grid item xs={12} md={6}>
              <Typography><strong>{t('common:amldd.birthPlace')}:</strong> {record.locaisNascimento}</Typography>
            </Grid>
          )}
          {record.enderecos && (
            <Grid item xs={12}>
              <Typography><strong>{t('common:amldd.addresses')}:</strong> {record.enderecos}</Typography>
            </Grid>
          )}
          {record.programas && (
            <Grid item xs={12}>
              <Typography><strong>{t('common:amldd.programs')}:</strong> {record.programas}</Typography>
            </Grid>
          )}
          {record.acusacoes && (
            <Grid item xs={12}>
              <Typography color="error"><strong>{t('common:amldd.accusations')}:</strong> {record.acusacoes}</Typography>
            </Grid>
          )}
          {record.procuradoPor && (
            <Grid item xs={12}>
              <Typography><strong>{t('common:amldd.wantedBy')}:</strong> {record.procuradoPor}</Typography>
            </Grid>
          )}
          {record.dataEntrada && (
            <Grid item xs={12}>
              <Typography variant="caption">
                <strong>{t('common:amldd.entryDate')}:</strong> {new Date(record.dataEntrada).toLocaleString()}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    )
  }

  // AMLDD_PEP specific fields
  if (checkType === 'AMLDD_PEP') {
    return (
      <Box>
        <Grid container spacing={2}>
          {record.classificacao && (
            <Grid item xs={12} md={6}>
              <Typography>
                <strong>{t('common:amldd.classification')}:</strong>{' '}
                {record.classificacao === 'T' ? t('common:amldd.holder') : t('common:amldd.related')}
              </Typography>
            </Grid>
          )}
          {record.score !== undefined && (
            <Grid item xs={12} md={6}>
              <Typography><strong>{t('common:amldd.score')}:</strong> {record.score}</Typography>
            </Grid>
          )}
          {record.documentos && (
            <Grid item xs={12}>
              <Typography><strong>{t('common:amldd.documents')}:</strong> {record.documentos}</Typography>
            </Grid>
          )}
          {record.descricao && (
            <Grid item xs={12}>
              <Typography><strong>{t('common:amldd.description')}:</strong> {record.descricao}</Typography>
            </Grid>
          )}
          {record.informacoesAdicional && (
            <Grid item xs={12}>
              <Typography><strong>{t('common:amldd.additionalInfo')}:</strong> {record.informacoesAdicional}</Typography>
            </Grid>
          )}
          {record.dataInicio && (
            <Grid item xs={12} md={6}>
              <Typography><strong>{t('common:amldd.startDate')}:</strong> {new Date(record.dataInicio).toLocaleDateString()}</Typography>
            </Grid>
          )}
          {record.dataFim && (
            <Grid item xs={12} md={6}>
              <Typography><strong>{t('common:amldd.endDate')}:</strong> {new Date(record.dataFim).toLocaleDateString()}</Typography>
            </Grid>
          )}
          {record.dataNascimentoFundacao && (
            <Grid item xs={12} md={6}>
              <Typography><strong>{t('common:amldd.birthDate')}:</strong> {new Date(record.dataNascimentoFundacao).toLocaleDateString()}</Typography>
            </Grid>
          )}
          {record.cidadeUfCargo && (
            <Grid item xs={12} md={6}>
              <Typography><strong>{t('common:amldd.location')}:</strong> {record.cidadeUfCargo}</Typography>
            </Grid>
          )}
          {record.oficial && (
            <Grid item xs={12}>
              <Typography><strong>{t('common:amldd.official')}:</strong> {record.oficial === 'S' ? t('common:yes') : t('common:no')}</Typography>
            </Grid>
          )}
          {record.dataObito && (
            <Grid item xs={12}>
              <Typography color="text.secondary">
                <strong>{t('common:amldd.deathDate')}:</strong> {new Date(record.dataObito).toLocaleDateString()}
              </Typography>
            </Grid>
          )}
          {record.dataAtualizacao && (
            <Grid item xs={12}>
              <Typography variant="caption">
                <strong>{t('common:amldd.lastUpdate')}:</strong> {new Date(record.dataAtualizacao).toLocaleString()}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    )
  }

  // AMLDD_MTLSN variants (Simple, Complete, With News)
  if (checkType === 'AMLDD_MTLSN' || checkType === 'AMLDD_MTLSN_SIMPLE' || checkType === 'AMLDD_MTLSN_WITH_NEWS') {
    const config = MTLSN_FIELDS_CONFIG[checkType]
    if (config) {
      return renderMtlsnFields(record, config, t)
    }
  }

  // Fallback: render as recursive property
  return (
    <RecursiveProperty
      property={record}
      excludeBottomBorder={false}
      rootProperty={true}
    />
  )
}

export default AmlddCheckDetail
